import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import Papa from 'papaparse';

interface FieldChangeEvent {
  fieldKey: string;
  value: string;
}

@Component({
  selector: 'app-job-request-test-psd-detail',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './job-request-test-psd-detail.component.html',
  styleUrl: './job-request-test-psd-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class JobRequestTestPsdDetailComponent {
  @Input({ required: true }) testKey = '';
  @Input({ required: true }) fieldValueByKey!: (testKey: string, fieldKey: string) => string;
  @Input({ required: true }) computedValueByKey!: (testKey: string, fieldKey: string) => string;

  protected readonly percentileRows = [5, 10, 25, 40, 50, 60, 75, 85, 90, 95];
  protected readonly sizePercentRows = [6, 44, 74];
  protected importMessage = '';
  protected importError = '';
  protected isImported = false;

  @Output() fieldChanged = new EventEmitter<FieldChangeEvent>();

  protected onFieldInput(fieldKey: string, value: string): void {
    this.fieldChanged.emit({ fieldKey, value });
  }

  protected openCsvFilePicker(event: Event, fileInput: HTMLInputElement): void {
    event.preventDefault();
    fileInput.click();
  }

  protected async onCsvSelected(event: Event): Promise<void> {
    this.importError = '';
    this.importMessage = '';

    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];

    if (!file) {
      return;
    }

    try {
      const text = await file.text();
      const parsed = this.parseCsv(text);
      const updatedCount = this.applyImportedValues(parsed.rows, parsed.delimiter);

      if (updatedCount <= 0) {
        this.importError = 'No PSD values were imported. Check CSV headers/columns and try again.';
      } else {
        this.isImported = true;
        this.importMessage = `Imported ${updatedCount} value${updatedCount === 1 ? '' : 's'} from ${file.name}.`;
      }
    } catch {
      this.importError = 'Failed to parse CSV file.';
    } finally {
      if (input) {
        input.value = '';
      }
    }
  }

  private applyImportedValues(rows: string[][], delimiter: string): number {
    if (!rows.length) {
      return 0;
    }

    const percentileKeys = this.percentileRows.map(percentile => `percentile${percentile}`);
    const lessThanKeys = this.percentileRows.map(percentile => `percentile${percentile}_size`);
    const diameterKeys = this.sizePercentRows.map(size => `diameter${size}`);
    const cumulativeKeys = this.sizePercentRows.map(size => `size${size}_percentile`);

    const headerIndex = this.findHeaderRow(rows);
    const sourceRows = headerIndex >= 0 ? rows.slice(headerIndex + 1) : rows;

    let percentileColumn = 0;
    let lessThanColumn = 1;
    let diameterColumn = 2;
    let cumulativeColumn = 3;

    if (headerIndex >= 0) {
      const headers = rows[headerIndex] ?? [];
      percentileColumn = this.findColumnIndex(headers, ['percentile']);
      lessThanColumn = this.findColumnIndex(headers, ['lessthan', 'lessthan', 'less']);
      diameterColumn = this.findColumnIndex(headers, ['diameter']);
      cumulativeColumn = this.findColumnIndex(headers, ['cumulativeless', 'cumulative', 'cumless']);
    }

    let updated = 0;

    // Excel-export convenience mapping: percentile values can come from B25:B34.
    const percentileFromExcelRange = this.importRange(percentileKeys, rows, 24, 1, delimiter, true);
    if (percentileFromExcelRange > 0) {
      updated += percentileFromExcelRange;
    } else {
      updated += this.importColumn(percentileKeys, sourceRows, percentileColumn, delimiter);
    }

    // Excel-export convenience mapping: % Less Than values can come from C25:C34.
    const lessThanFromExcelRange = this.importRange(lessThanKeys, rows, 24, 2, delimiter);
    if (lessThanFromExcelRange > 0) {
      updated += lessThanFromExcelRange;
    } else {
      updated += this.importColumn(lessThanKeys, sourceRows, lessThanColumn, delimiter);
    }
    // Excel-export convenience mapping: diameter values can come from B37:B39.
    const diameterFromExcelRange = this.importRange(diameterKeys, rows, 36, 1, delimiter, true);
    if (diameterFromExcelRange > 0) {
      updated += diameterFromExcelRange;
    } else {
      updated += this.importColumn(diameterKeys, sourceRows, diameterColumn, delimiter);
    }
    // Excel-export convenience mapping: Cumulative Less % values can come from C37:C39.
    const cumulativeFromExcelRange = this.importRange(cumulativeKeys, rows, 36, 2, delimiter);
    if (cumulativeFromExcelRange > 0) {
      updated += cumulativeFromExcelRange;
    } else {
      updated += this.importColumn(cumulativeKeys, sourceRows, cumulativeColumn, delimiter);
    }

    return updated;
  }

  private importColumn(keys: string[], rows: string[][], columnIndex: number, delimiter: string): number {
    if (columnIndex < 0) {
      return 0;
    }

    let changed = 0;
    for (let index = 0; index < keys.length; index++) {
      const row = rows[index];
      if (!row) {
        break;
      }

      const raw = row[columnIndex] ?? '';
      const value = this.normalizeImportedValue(raw, delimiter);
      this.onFieldInput(keys[index], value);
      changed++;
    }

    return changed;
  }

  private importRange(keys: string[], rows: string[][], startRowIndex: number, columnIndex: number, delimiter: string, skipToFirstValue = false): number {
    if (startRowIndex < 0 || columnIndex < 0) {
      return 0;
    }

    let effectiveStartRowIndex = startRowIndex;
    if (skipToFirstValue) {
      const firstValueOffset = this.findFirstNonEmptyValueOffset(rows, startRowIndex, columnIndex, keys.length + 2, delimiter);
      if (firstValueOffset === -1) {
        return 0;
      }

      effectiveStartRowIndex += firstValueOffset;
    }

    let changed = 0;
    for (let index = 0; index < keys.length; index++) {
      const row = rows[effectiveStartRowIndex + index];
      if (!row) {
        break;
      }

      const raw = row[columnIndex] ?? '';
      const value = this.normalizeImportedValue(raw, delimiter);
      if (!value) {
        continue;
      }

      this.onFieldInput(keys[index], value);
      changed++;
    }

    return changed;
  }

  private findFirstNonEmptyValueOffset(rows: string[][], startRowIndex: number, columnIndex: number, maxDepth: number, delimiter: string): number {
    for (let offset = 0; offset < Math.max(maxDepth, 1); offset++) {
      const row = rows[startRowIndex + offset];
      if (!row) {
        break;
      }

      const value = this.normalizeImportedValue(row[columnIndex] ?? '', delimiter);
      if (value) {
        return offset;
      }
    }

    return -1;
  }

  private parseCsv(content: string): { rows: string[][]; delimiter: string } {
    const normalized = content.replace(/^\uFEFF/, '');

    const parsed = Papa.parse<string[]>(normalized, {
      skipEmptyLines: false
    });

    const rows = (parsed.data ?? []).map(row => row.map(cell => (cell ?? '').trim()));
    if (rows.length === 0) {
      throw new Error('CSV parse error');
    }

    const delimiter = parsed.meta.delimiter || ',';
    return { rows, delimiter };
  }

  private findHeaderRow(rows: string[][]): number {
    for (let rowIndex = 0; rowIndex < Math.min(rows.length, 5); rowIndex++) {
      const row = rows[rowIndex] ?? [];
      const normalized = row.map(cell => this.normalizeHeaderCell(cell));
      const hasRelevantHeaders = normalized.some(cell =>
        cell.includes('percentile')
        || cell.includes('lessthan')
        || cell.includes('diameter')
        || cell.includes('cumulative')
      );

      if (hasRelevantHeaders) {
        return rowIndex;
      }
    }

    return -1;
  }

  private findColumnIndex(headers: string[], keywords: string[]): number {
    const normalizedHeaders = headers.map(cell => this.normalizeHeaderCell(cell));
    return normalizedHeaders.findIndex(cell => keywords.some(keyword => cell.includes(keyword)));
  }

  private normalizeHeaderCell(value: string): string {
    return value.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private normalizeImportedValue(rawValue: string, delimiter: string): string {
    const trimmed = rawValue.trim();
    if (!trimmed) {
      return '';
    }

    if (delimiter === ';' && /^-?\d+,\d+$/.test(trimmed)) {
      return trimmed.replace(',', '.');
    }

    return trimmed;
  }
}
